import * as jsondiffpatch from 'jsondiffpatch'
import type { Config } from '@dashboard/types/config'
import { VOLATILE_CONFIG_KEYS, isStampedEntityPath } from './configMetadata'
import { safeLiquidRender } from './services/liquidUtils'

export interface ConfigChange {
  category: string
  type: 'added' | 'modified' | 'deleted'
  name: string
  id: string
  details?: string[] // Array of specific changes like "Title changed", "Added 2 segments"
  diff?: any // The actual jsondiffpatch diff for this item (for AI analysis)
  /** Present on hosted page, site style and project script changes, which the server computes. */
  hosted?: HostedConfigChange
}

/** One pending hosted change as `GET hosted/site` and the debugger publish state report it. */
export interface HostedChange {
  kind: 'page' | 'site_style' | 'scripts'
  type: 'added' | 'modified' | 'moved'
  name: string
  pageId: string | null
  path: string | null
  previousPath: string | null
  affectedPages: number
  placeholders: number
  /** Campaign changes that target an element this page edit removes; the server words each message. */
  orphanedChanges: OrphanedCampaignChange[]
}

export interface OrphanedCampaignChange {
  campaignId: string
  campaign: string
  audience: string
  version: string | null
  elementId: string
  live: boolean
  message: string
}

export interface HostedConfigChange extends HostedChange {
  /** The project's hosted host, so the review can name the URL a page goes live at. */
  servingHost: string | null
}

const HOSTED_CATEGORIES: Record<HostedChange['kind'], string> = {
  page: 'Pages',
  site_style: 'Site styles',
  scripts: 'Project scripts'
}

// Hosted changes join the publish list after the config's, in server order, so the
// PublishBar and the debugger count them the same way.
export const hostedConfigChanges = (changes: readonly HostedChange[], servingHost: string | null): ConfigChange[] =>
  changes.map((change) => ({
    category: HOSTED_CATEGORIES[change.kind],
    type: change.type === 'added' ? 'added' : 'modified',
    name: change.name,
    id: change.pageId ?? change.kind,
    hosted: { ...change, servingHost }
  }))

// Array items are matched by `id`, never by position: removing or inserting one
// offer must not turn every later offer into a modification. Items without an
// `id` keep positional matching; reordered items surface as jsondiffpatch moves.
const configDiffer = jsondiffpatch.create({
  objectHash: (item: object, index?: number) => {
    const id = item && 'id' in item ? item.id : undefined
    return typeof id === 'string' || typeof id === 'number' ? `id:${id}` : `index:${index}`
  },
  arrays: { detectMove: true, includeValueOnMove: false }
})

interface SplitTest {
  pid: string
  resource_type: string
  resource_id: string
  status: string
  variants: any[]
  created_at: string
  updated_at: string
  started_at: string | null
}

interface Integration {
  name: string
  label: string
  hasExistingIntegration: boolean
  lastPingAt?: string | null
  created_at?: string
  updated_at?: string
}

interface IntegrationConnection {
  name: string
  label: string
  hasExistingIntegration: boolean
  created_at?: string
  updated_at?: string
  settings_updated_at?: string
}

export interface PublishContext {
  splitTests: SplitTest[]
  integrations: { v1: Integration[]; v2: IntegrationConnection[] }
  team: {
    last_published_at?: string | Date | null
    recaptcha_key?: string | null
    published_recaptcha_key?: string | null
    has_recaptcha_secret: boolean
    uses_v2_integrations?: boolean
  } | null
}

// Match the dashboard's plain-text labels, including HTML entity decoding.
const stripHtml = (x: string | null): string => {
  if (!x) {
    return ''
  }
  const div = document.createElement('div')
  div.innerHTML = x
  return div.innerText ?? div.textContent ?? ''
}

// Resolve question identity against the snapshot being analyzed, never a dashboard store.
const isAQuestion = (segmentGroup: Config['dimensions'][number], config: Config): boolean => {
  const question = config.rightAsk?.questions?.find((question) => question.dimensionId === segmentGroup.id)
  if (!question) {
    return false
  }
  if (question.isQuestion === true) {
    return true
  }
  if (!question.text) {
    return false
  }
  const tmp = document.createElement('DIV')
  tmp.innerHTML = question.text
  let text = tmp.innerHTML.replace(/{{ phrases\.(\w+) }}/g, (match, phraseId) => {
    const phrase = config.phrases?.find((phrase) => phrase.id === phraseId)
    return phrase ? phrase.fallbackText : match
  })
  text = safeLiquidRender(text, {})
  tmp.innerHTML = text
  text = tmp.textContent || tmp.innerText || ''
  return text !== ''
}

// Only the collections the reminder reads. Callers can pass the full config.
type SplitTestNameSource = {
  rightCta?: {
    offers?: { id?: string; name?: string | null }[] | null
    offerFunnels?: { id?: string; name?: string | null }[] | null
  } | null
  rightAsk?: {
    questions?: { id?: string; text?: string | null; dimensionId?: string | null }[] | null
  } | null
  dimensions?: { id?: string; name?: string | null }[] | null
}

// The reminder only names a test whose resource is still in the config. A deleted
// flow must not surface as "Unknown flow". Blank names stay the historical fallback
// for other callers via getSplitTestResourceName.
export const splitTestReminderName = (
  test: Pick<SplitTest, 'resource_type' | 'resource_id'>,
  config: SplitTestNameSource
): string | null => {
  if (test.resource_type === 'offer') {
    const offer = config.rightCta?.offers?.find((offer) => offer.id === test.resource_id)
    if (!offer) {
      return null
    }
    return offer.name?.trim() || 'Untitled offer'
  }
  if (test.resource_type === 'flow') {
    const flow = config.rightCta?.offerFunnels?.find((flow) => flow.id === test.resource_id)
    if (!flow) {
      return null
    }
    return flow.name?.trim() || 'Untitled flow'
  }
  if (test.resource_type !== 'question') {
    return null
  }
  const question = config.rightAsk?.questions?.find((question) => question.id === test.resource_id)
  if (!question) {
    return null
  }
  if (question.dimensionId) {
    const segmentGroup = config.dimensions?.find((dimension) => dimension.id === question.dimensionId)
    return segmentGroup?.name?.trim() || 'Untitled question'
  }
  return stripHtml(question.text ?? null).trim() || 'Untitled question'
}

// Get a human-readable name for a split test resource
export const getSplitTestResourceName = (test: SplitTest, config: Config): string => {
  if (test.resource_type === 'offer') {
    const offer = config.rightCta?.offers?.find((offer) => offer.id === test.resource_id)
    return offer?.name || 'Unknown offer'
  }
  if (test.resource_type === 'flow') {
    return config.rightCta?.offerFunnels?.find((flow) => flow.id === test.resource_id)?.name || 'Unknown flow'
  }
  const question = config.rightAsk?.questions?.find((question) => question.id === test.resource_id)
  if (question?.dimensionId) {
    const segmentGroup = config.dimensions?.find((dimension) => dimension.id === question.dimensionId)
    return segmentGroup?.name || 'Unknown question'
  }
  return stripHtml(question?.text ?? null) || 'Unknown question'
}

// Check if split tests have changed since last publish
export const checkSplitTestChanges = (config: Config, context: PublishContext): ConfigChange[] => {
  const changes: ConfigChange[] = []

  // Get current active split tests
  const activeSplitTests = context.splitTests.filter((test) => test.status === 'active')

  // Get the last published timestamp from the team
  const lastPublishedAt = context.team?.last_published_at

  if (!lastPublishedAt) {
    // If never published, all active tests need publishing
    activeSplitTests.forEach((test) => {
      const resourceType = test.resource_type === 'offer' ? 'Offer' : 'Question'
      const resourceName = getSplitTestResourceName(test, config)
      changes.push({
        category: 'Split Tests',
        type: 'added',
        name: `Testing ${resourceType}: ${resourceName}`,
        id: test.pid
      })
    })
    return changes
  }

  // Compare each active test's timestamps with last publish time
  const lastPublished = new Date(lastPublishedAt)

  activeSplitTests.forEach((test) => {
    // Check if test was created after last publish
    const createdAt = new Date(test.created_at)
    const updatedAt = new Date(test.updated_at)
    const startedAt = test.started_at ? new Date(test.started_at) : null

    // Test needs publishing if:
    // 1. Created after last publish, OR
    // 2. Started after last publish (test was activated), OR
    // 3. Updated after last publish (variants changed)
    const needsPublish =
      createdAt > lastPublished || (startedAt && startedAt > lastPublished) || updatedAt > lastPublished

    if (needsPublish) {
      const resourceType = test.resource_type === 'offer' ? 'Offer' : 'Question'
      const resourceName = getSplitTestResourceName(test, config)

      // Determine if it's new or modified
      const isNew = createdAt > lastPublished

      changes.push({
        category: 'Split Tests',
        type: isNew ? 'added' : 'modified',
        name: `Testing ${resourceType}: ${resourceName}`,
        id: test.pid,
        details: isNew ? undefined : ['Test configuration changed']
      })
    }
  })

  return changes
}

// Check if integrations have changed since last publish
export const checkIntegrationChanges = (context: PublishContext): ConfigChange[] => {
  const changes: ConfigChange[] = []

  // Get current integrations
  const v1 = context.team?.uses_v2_integrations
    ? []
    : context.integrations.v1.filter((integration) => integration.hasExistingIntegration)
  const v2 = context.team?.uses_v2_integrations
    ? context.integrations.v2.filter((connection) => connection.hasExistingIntegration)
    : []

  // Get the last published timestamp from the team
  const lastPublishedAt = context.team?.last_published_at

  if (!lastPublishedAt) {
    // If never published, all integrations need publishing
    v1.forEach((integration) => {
      changes.push({
        category: 'Integrations',
        type: 'added',
        name: `${integration.label} integration`,
        id: integration.name
      })
    })

    v2.forEach((connection) => {
      changes.push({
        category: 'Integrations',
        type: 'added',
        name: `${connection.label} integration`,
        id: connection.name
      })
    })

    return changes
  }

  const lastPublished = new Date(lastPublishedAt)

  // Check V1 integrations
  v1.forEach((integration) => {
    if (!integration.created_at || !integration.updated_at) {
      return
    }

    const createdAt = new Date(integration.created_at)
    const updatedAt = new Date(integration.updated_at)

    // Integration needs publishing if created or updated after last publish
    const needsPublish = createdAt > lastPublished || updatedAt > lastPublished

    if (needsPublish) {
      const isNew = createdAt > lastPublished

      changes.push({
        category: 'Integrations',
        type: isNew ? 'added' : 'modified',
        name: `${integration.label} integration`,
        id: integration.name,
        details: isNew ? undefined : ['Integration settings changed']
      })
    }
  })

  // Check V2 integrations - use settings_updated_at to ignore OAuth token refreshes
  v2.forEach((connection) => {
    if (!connection.created_at) {
      return
    }

    const createdAt = new Date(connection.created_at)
    // Use settings_updated_at (ignores token refreshes) with fallback to created_at
    const settingsUpdatedAt = connection.settings_updated_at ? new Date(connection.settings_updated_at) : createdAt

    // Integration needs publishing if created or settings changed after last publish
    const needsPublish = createdAt > lastPublished || settingsUpdatedAt > lastPublished

    if (needsPublish) {
      const isNew = createdAt > lastPublished

      changes.push({
        category: 'Integrations',
        type: isNew ? 'added' : 'modified',
        name: `${connection.label} integration`,
        id: connection.name,
        details: isNew ? undefined : ['Integration settings changed']
      })
    }
  })

  return changes
}

// Check if team settings (like Turnstile keys) have changed since last publish
export const checkTeamSettingsChanges = (team: PublishContext['team']): ConfigChange[] => {
  const changes: ConfigChange[] = []

  if (!team) {
    return changes
  }

  const lastPublishedAt = team.last_published_at
  const currentKey = team.recaptcha_key || ''
  const publishedKey = team.published_recaptcha_key || ''

  if (!lastPublishedAt) {
    // First publish: report if Turnstile is configured
    if (currentKey || team.has_recaptcha_secret) {
      changes.push({
        category: 'Bot Protection',
        type: 'added',
        name: 'Cloudflare Turnstile configuration',
        id: 'team.turnstile'
      })
    }
  } else if (currentKey !== publishedKey) {
    // After first publish: detect key changes by comparing against published snapshot
    if (currentKey && !publishedKey) {
      // Turnstile newly configured since last publish
      changes.push({
        category: 'Bot Protection',
        type: 'added',
        name: 'Cloudflare Turnstile configuration',
        id: 'team.turnstile'
      })
    } else if (!currentKey && publishedKey) {
      // Turnstile removed since last publish
      changes.push({
        category: 'Bot Protection',
        type: 'deleted',
        name: 'Cloudflare Turnstile configuration',
        id: 'team.turnstile'
      })
    } else {
      // Turnstile key changed since last publish
      changes.push({
        category: 'Bot Protection',
        type: 'modified',
        name: 'Cloudflare Turnstile configuration',
        id: 'team.turnstile',
        details: ['Site key changed']
      })
    }
  }

  return changes
}

// A diff made only of volatile metadata stamps is not a change. `modifiedAt`
// moves only alongside a real edit (the edit itself is what makes the config
// dirty), and an edit followed by a revert leaves only the stamp different.
const isOnlyVolatileChanges = (diff: unknown): boolean => {
  // If diff is null or undefined, there are no changes
  if (!diff) {
    return true
  }

  // Function to recursively check objects
  // `path` is the diff key trail; a `modifiedAt` stamp is metadata only on a
  // flow or widget (rightCta.offerFunnels[i] / rightCta.widgets[i]).
  const checkObject = (obj: unknown, path: string[]): boolean => {
    // If it's not an object or is null, it's not a volatile-key change
    if (!obj || typeof obj !== 'object') {
      return false
    }

    const entries: [string, unknown][] = Object.entries(obj)
    for (const [key, value] of entries) {
      // Skip the _t property which is used by jsondiffpatch for arrays
      if (key === '_t') {
        continue
      }

      // A volatile key's own delta: [new] added, [old, new] modified, [old, 0, 0] deleted
      if (Array.isArray(value) && (key === 'accessedAt' || (key === 'modifiedAt' && isStampedEntityPath(path)))) {
        continue
      }
      // Nested objects may hold only volatile changes; anything else is a real change
      if (!value || typeof value !== 'object' || Array.isArray(value) || !checkObject(value, [...path, key])) {
        return false
      }
    }

    // If we got here, all changes were volatile keys or objects containing only volatile-key changes
    return true
  }

  return checkObject(diff, [])
}

// Check if two configs have meaningful differences (excluding volatile stamps)
export const hasChanges = (currentConfig: Config | null, publishedConfig: Config | null): boolean => {
  if (!currentConfig || !publishedConfig) {
    return !!currentConfig !== !!publishedConfig
  }

  const diff = configDiffer.diff(publishedConfig, currentConfig)
  if (!diff) {
    return false
  }

  return !isOnlyVolatileChanges(diff)
}

// Helper to strip HTML from diff objects for cleaner AI analysis
export const stripHtmlFromDiff = (diff: any): any => {
  if (!diff || typeof diff !== 'object') {
    return diff
  }

  // Handle arrays (jsondiffpatch format)
  if (Array.isArray(diff)) {
    return diff.map((item) => {
      if (typeof item === 'string') {
        return stripHtml(item)
      }
      return item
    })
  }

  // Handle objects
  const cleaned: any = {}
  for (const key of Object.keys(diff)) {
    const value = diff[key]
    if (typeof value === 'string') {
      cleaned[key] = stripHtml(value)
    } else if (typeof value === 'object') {
      cleaned[key] = stripHtmlFromDiff(value)
    } else {
      cleaned[key] = value
    }
  }
  return cleaned
}

// Helper to extract what fields changed in a modified object
// Called with one entity's diff; only that level drops stamp keys, so nested
// content keys that happen to be named `modifiedAt` (campaign attributes) still count.
const getModifiedFields = (diff: any, ignoredFields: readonly string[] = VOLATILE_CONFIG_KEYS): string[] => {
  if (!diff || typeof diff !== 'object') {
    return []
  }

  const changes: string[] = []
  const fieldLabels: Record<string, string> = {
    name: 'Name',
    question: 'Question',
    text: 'Question text',
    segments: 'Segments',
    signals: 'Signals',
    syncTo: 'ESP sync',
    syncToCustomField: 'Custom field',
    syncToField: 'Sync field',
    parts: 'Content',
    title: 'Title',
    description: 'Description',
    buttonText: 'Button text',
    triggeredAction: 'Action',
    funnelDefinition: 'Flow structure',
    is_active: 'Active status',
    testing: 'A/B test settings',
    variants: 'Variants',
    options: 'Answer options',
    displayType: 'Display type',
    displayOptions: 'Display options',
    otherOptions: 'Other option settings',
    isRandomizingOptionsOrder: 'Randomize options',
    inputType: 'Input type',
    inputSettings: 'Input settings',
    dimensionId: 'Linked dimension',
    visibility: 'Visibility rules',
    isEnabled: 'Enabled status',
    // Attribution fields
    destination: 'Sync destination',
    overwrite: 'Overwrite setting',
    label: 'Label',
    value: 'Value template',
    internal: 'Internal flag'
  }

  for (const key in diff) {
    if (key === '_t') {
      continue
    } // Skip jsondiffpatch metadata
    if (ignoredFields.includes(key)) {
      continue
    } // Skip ignored fields

    const label = fieldLabels[key] || key
    const value = diff[key]

    if (Array.isArray(value)) {
      // jsondiffpatch array format:
      // [newValue] = added
      // [oldValue, newValue] = modified
      // [oldValue, 0, 0] = deleted
      if (value.length === 1) {
        // Added field
        changes.push(label)
      } else if (value.length === 2) {
        // Modified field
        changes.push(label)
      } else if (value.length === 3 && value[1] === 0 && value[2] === 0) {
        // Deleted field
        changes.push(label)
      } else if (value.length === 3 && value[2] === 3) {
        // Moved array item (reordered answers, fields, steps)
        changes.push(label)
      }
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      // Nested changes - recurse
      const nestedChanges = getModifiedFields(value, ['accessedAt'])
      if (nestedChanges.length > 0) {
        changes.push(label)
      }
    }
  }

  return changes
}

// Pure config-vs-config diff. Unlike analyzeChanges, this does NOT append
// split test / integration / team settings changes — those are computed
// against the team's CURRENT published state, so they only make sense in
// the publish flow, not when comparing two arbitrary snapshots (e.g. the
// version history drawer).
export const analyzeConfigOnlyChanges = (currentConfig: Config, publishedConfig: Config | null): ConfigChange[] => {
  if (!publishedConfig) {
    // First publish - everything is new
    return analyzeNewConfig(currentConfig)
  }

  const diff = configDiffer.diff(publishedConfig, currentConfig)

  return diff ? parseConfigDiff(diff, currentConfig, publishedConfig) : []
}

// Preserve publish ordering and the dashboard's first-publish short circuit.
export const analyzeChanges = (
  currentConfig: Config,
  publishedConfig: Config | null,
  context: PublishContext
): ConfigChange[] => {
  if (!publishedConfig) {
    return [...checkTeamSettingsChanges(context.team), ...analyzeNewConfig(currentConfig)]
  }

  const configChanges = analyzeConfigOnlyChanges(currentConfig, publishedConfig)
  const splitTestChanges = checkSplitTestChanges(currentConfig, context)
  const integrationChanges = checkIntegrationChanges(context)
  const teamSettingsChanges = checkTeamSettingsChanges(context.team)

  return [...configChanges, ...splitTestChanges, ...integrationChanges, ...teamSettingsChanges]
}

const analyzeNewConfig = (config: Config): ConfigChange[] => {
  const changes: ConfigChange[] = []

  // Count dimensions/segment groups (exclude questions)
  if (config.dimensions?.length > 0) {
    config.dimensions.forEach((dim) => {
      // Use isAQuestion utility to properly determine if this is a question
      if (!isAQuestion(dim, config)) {
        changes.push({
          category: 'Segment Groups',
          type: 'added',
          name: dim.name || 'Unnamed Segment Group',
          id: dim.id
        })
      }
    })
  }

  // Count campaigns
  if (config.campaigns?.length > 0) {
    config.campaigns.forEach((campaign) => {
      changes.push({
        category: 'Campaigns',
        type: 'added',
        name: campaign.name || 'Unnamed Campaign',
        id: campaign.id
      })
    })
  }

  // Count offers
  const offers = config.rightCta?.offers ?? []
  if (offers.length > 0) {
    offers.forEach((offer) => {
      changes.push({
        category: 'Offers / Forms',
        type: 'added',
        name: offer.name || 'Unnamed Offer',
        id: offer.id
      })
    })
  }

  // Count flows
  const offerFunnels = config.rightCta?.offerFunnels ?? []
  if (offerFunnels.length > 0) {
    offerFunnels.forEach((flow) => {
      changes.push({
        category: 'Flows',
        type: 'added',
        name: flow.name || 'Unnamed Flow',
        id: flow.id
      })
    })
  }

  // Count widgets
  const widgets = config.rightCta?.widgets ?? []
  if (widgets.length > 0) {
    widgets.forEach((widget) => {
      changes.push({
        category: 'Widgets',
        type: 'added',
        name: widget.name || 'Unnamed Widget',
        id: widget.id
      })
    })
  }

  // Count goals
  if (config.goals?.length > 0) {
    config.goals.forEach((goal) => {
      changes.push({
        category: 'Goals',
        type: 'added',
        name: goal.name || 'Unnamed Goal',
        id: goal.id
      })
    })
  }

  // Count questions
  if (config.rightAsk?.questions?.length > 0) {
    config.rightAsk.questions.forEach((question) => {
      changes.push({
        category: 'Questions',
        type: 'added',
        name: stripHtml(question.text || '') || 'Unnamed Question',
        id: question.id
      })
    })
  }

  for (const domain of config.domainNames ?? []) {
    changes.push({ category: 'Domains', type: 'added', name: domain, id: `domain:${domain}` })
  }

  return changes
}

// One entity collection compared by identity: present only now is added, present
// only before is deleted, present in both with a real edit is modified.
const parseCollectionChanges = <T extends object>(
  category: string,
  published: readonly T[],
  current: readonly T[],
  identify: (item: T, index: number) => string,
  nameOf: (item: T) => string
): ConfigChange[] => {
  const changes: ConfigChange[] = []
  const publishedById = new Map(published.map((item, index) => [identify(item, index), item]))
  const currentIds = new Set(current.map(identify))

  current.forEach((item, index) => {
    const id = identify(item, index)
    const before = publishedById.get(id)
    if (!before) {
      changes.push({ category, type: 'added', name: nameOf(item), id })
      return
    }
    const diff = configDiffer.diff(before, item)
    const details = diff ? getModifiedFields(diff) : []
    if (details.length > 0) {
      changes.push({
        category,
        type: 'modified',
        name: nameOf(item),
        id,
        details,
        diff
      })
    }
  })

  published.forEach((item, index) => {
    const id = identify(item, index)
    if (!currentIds.has(id)) {
      changes.push({ category, type: 'deleted', name: nameOf(item), id })
    }
  })

  return changes
}

// Domains are plain strings, matched by value. The first one is the primary
// domain, so a new first domain is a change even when the set is unchanged.
const parseDomainChanges = (published: readonly string[], current: readonly string[]): ConfigChange[] => {
  const changes: ConfigChange[] = []
  current.forEach((domain) => {
    if (!published.includes(domain)) {
      changes.push({ category: 'Domains', type: 'added', name: domain, id: `domain:${domain}` })
    }
  })
  published.forEach((domain) => {
    if (!current.includes(domain)) {
      changes.push({ category: 'Domains', type: 'deleted', name: domain, id: `domain:${domain}` })
    }
  })
  const [publishedPrimary] = published
  const [currentPrimary] = current
  if (publishedPrimary && currentPrimary && publishedPrimary !== currentPrimary) {
    changes.push({
      category: 'Domains',
      type: 'modified',
      name: 'Primary domain',
      id: 'domainNames.primary',
      details: [`${currentPrimary} is now the primary domain`]
    })
  }
  return changes
}

const enabledAnalyticsTools = (config: Config): string =>
  (Array.isArray(config.settings?.integrations) ? config.settings.integrations : [])
    .filter((entry) => entry.enabled)
    .map((entry) => entry.name)
    .sort()
    .join(',')

const parseConfigDiff = (diff: any, currentConfig: Config, publishedConfig: Config): ConfigChange[] => {
  const changes: ConfigChange[] = []
  const byId = (item: { id: string }) => item.id

  if (diff.dimensions) {
    changes.push(...parseDimensionsChanges(diff.dimensions, currentConfig, publishedConfig))
  }

  if (diff.campaigns) {
    changes.push(
      ...parseCollectionChanges(
        'Campaigns',
        publishedConfig.campaigns ?? [],
        currentConfig.campaigns ?? [],
        byId,
        (campaign) => campaign.name || 'Unnamed Campaign'
      )
    )
  }

  if (diff.rightCta) {
    const published = publishedConfig.rightCta
    const current = currentConfig.rightCta
    const rightCtaReplaced = Array.isArray(diff.rightCta)
    if (rightCtaReplaced || diff.rightCta.offers) {
      changes.push(
        ...parseCollectionChanges(
          'Offers / Forms',
          published?.offers ?? [],
          current?.offers ?? [],
          byId,
          (offer) => offer.name || 'Unnamed Offer'
        )
      )
    }
    if (rightCtaReplaced || diff.rightCta.offerFunnels) {
      changes.push(
        ...parseCollectionChanges(
          'Flows',
          published?.offerFunnels ?? [],
          current?.offerFunnels ?? [],
          byId,
          (flow) => flow.name || 'Unnamed Flow'
        )
      )
    }
    if (rightCtaReplaced || diff.rightCta.widgets) {
      changes.push(
        ...parseCollectionChanges(
          'Widgets',
          published?.widgets ?? [],
          current?.widgets ?? [],
          byId,
          (widget) => widget.name || 'Unnamed Widget'
        )
      )
    }
    if (!rightCtaReplaced && diff.rightCta.flowPriority) {
      changes.push({ category: 'Flows', type: 'modified', name: 'Flow priority', id: 'rightCta.flowPriority' })
    }
    // rightCta.settings: powered-by badge, widget colors, etc.
    if (!rightCtaReplaced && diff.rightCta.settings) {
      changes.push({
        category: 'Widget Settings',
        type: 'modified',
        name: 'Widget configuration settings',
        id: 'rightCta.settings'
      })
    }
  }

  if (diff.goals) {
    changes.push(
      ...parseCollectionChanges(
        'Goals',
        publishedConfig.goals ?? [],
        currentConfig.goals ?? [],
        byId,
        (goal) => goal.name || 'Unnamed Goal'
      )
    )
  }

  if (diff.phrases) {
    changes.push(
      ...parseCollectionChanges(
        'Phrases',
        publishedConfig.phrases ?? [],
        currentConfig.phrases ?? [],
        byId,
        (phrase) => phrase.fallbackText || 'Unnamed Phrase'
      )
    )
  }

  if (diff.rightAsk) {
    changes.push(...parseQuestionsChanges(diff.rightAsk, currentConfig, publishedConfig))
  }

  // Check settings - only report if it's an actual change, not just being added for the first time
  if (diff.settings && !Array.isArray(diff.settings)) {
    // Check if it's just empty jsHooks being initialized (not a meaningful change)
    const isOnlyEmptyJsHooks =
      diff.settings.jsHooks &&
      Object.keys(diff.settings).length === 1 &&
      Object.values(diff.settings.jsHooks).every(
        (value) =>
          // Check if it's an added empty string: [""]
          Array.isArray(value) && value.length === 1 && value[0] === ''
      )
    // Switching an analytics tool on and back off leaves `{ enabled: false }`, which reads the same as no entry.
    const isOnlySameAnalyticsTools =
      diff.settings.integrations &&
      Object.keys(diff.settings).length === 1 &&
      enabledAnalyticsTools(currentConfig) === enabledAnalyticsTools(publishedConfig)

    if (!isOnlyEmptyJsHooks && !isOnlySameAnalyticsTools) {
      changes.push({
        category: 'Settings',
        type: 'modified',
        name: 'Configuration settings',
        id: 'settings'
      })
    }
  }

  // Lead attribution rows have no identity; they stay matched by position.
  if (diff.attributes) {
    changes.push(
      ...parseCollectionChanges(
        'Attribution',
        publishedConfig.attributes ?? [],
        currentConfig.attributes ?? [],
        (_attribute, index) => index.toString(),
        (attribute) => attribute.label || 'Unnamed Attribute'
      )
    )
  }

  if (diff.domainNames) {
    changes.push(...parseDomainChanges(publishedConfig.domainNames ?? [], currentConfig.domainNames ?? []))
  }

  return changes
}

const parseDimensionsChanges = (
  dimensionsDiff: any,
  currentConfig: Config,
  publishedConfig: Config
): ConfigChange[] => {
  const changes: ConfigChange[] = []

  if (dimensionsDiff._t === 'a') {
    // Array diff - use ID-based matching instead of index-based to avoid false positives
    const currentDimensions = currentConfig.dimensions || []
    const publishedDimensions = publishedConfig.dimensions || []

    // Create maps for efficient lookup
    const currentById = new Map(currentDimensions.map((d) => [d.id, d]))
    const publishedById = new Map(publishedDimensions.map((d) => [d.id, d]))

    // Find truly added dimensions
    currentDimensions.forEach((dim) => {
      if (!publishedById.has(dim.id) && !isAQuestion(dim, currentConfig)) {
        changes.push({
          category: 'Segment Groups',
          type: 'added',
          name: dim.name || 'Unnamed Segment Group',
          id: dim.id
        })
      }
    })

    // Find truly deleted dimensions
    publishedDimensions.forEach((dim) => {
      if (!currentById.has(dim.id) && !isAQuestion(dim, currentConfig)) {
        changes.push({
          category: 'Segment Groups',
          type: 'deleted',
          name: dim.name || 'Unnamed Segment Group',
          id: dim.id
        })
      }
    })

    // Find truly modified dimensions (by comparing actual content, not index)
    currentDimensions.forEach((currentDim) => {
      const publishedDim = publishedById.get(currentDim.id)
      if (publishedDim) {
        // Compare the actual objects to see if there are real changes
        const diff = configDiffer.diff(publishedDim, currentDim)
        if (diff) {
          const modifiedFields = getModifiedFields(diff)
          if (modifiedFields.length > 0) {
            // Use isAQuestion utility to properly determine if this is a question or segment group
            const hasValidQuestion = isAQuestion(currentDim, currentConfig)

            // Get the display name - use question text if it's a question, otherwise dimension name
            const linkedQuestion = currentConfig.rightAsk?.questions?.find((q) => q.dimensionId === currentDim.id)
            const displayName =
              hasValidQuestion && linkedQuestion?.text
                ? stripHtml(linkedQuestion.text)
                : currentDim.name || 'Unnamed Segment Group'

            changes.push({
              category: hasValidQuestion ? 'Questions' : 'Segment Groups',
              type: 'modified',
              name: displayName,
              id: currentDim.id,
              details: modifiedFields,
              diff
            })
          }
        }
      }
    })
  } else {
    // Handle non-array-diff format
    const currentDimensions = currentConfig.dimensions || []
    const publishedDimensions = publishedConfig.dimensions || []

    if (currentDimensions.length > publishedDimensions.length) {
      currentDimensions.forEach((dim) => {
        const existsInPublished = publishedDimensions.some((pd) => pd.id === dim.id)
        if (!existsInPublished && !isAQuestion(dim, currentConfig)) {
          changes.push({
            category: 'Segment Groups',
            type: 'added',
            name: dim.name || 'Unnamed Segment Group',
            id: dim.id
          })
        }
      })
    }
  }

  return changes
}

const parseQuestionsChanges = (rightAskDiff: any, currentConfig: Config, publishedConfig: Config): ConfigChange[] => {
  const changes: ConfigChange[] = []

  // Helper to check if a question has meaningful text
  const hasValidQuestionText = (question: any): boolean => {
    if (!question.text) {
      return false
    }
    const cleanText = stripHtml(question.text).trim()
    return cleanText.length > 0
  }

  // Check if there are changes to the questions array
  if (rightAskDiff.questions) {
    const currentQuestions = currentConfig.rightAsk?.questions || []
    const publishedQuestions = publishedConfig.rightAsk?.questions || []

    // Create maps for efficient ID-based lookup
    const currentById = new Map(currentQuestions.map((q) => [q.id, q]))
    const publishedById = new Map(publishedQuestions.map((q) => [q.id, q]))

    // Find added questions (but only if they have meaningful text)
    currentQuestions.forEach((question) => {
      if (!publishedById.has(question.id) && hasValidQuestionText(question)) {
        changes.push({
          category: 'Questions',
          type: 'added',
          name: stripHtml(question.text || '') || 'Unnamed Question',
          id: question.id
        })
      }
    })

    // Find deleted questions (but only if they had meaningful text)
    publishedQuestions.forEach((question) => {
      if (!currentById.has(question.id) && hasValidQuestionText(question)) {
        changes.push({
          category: 'Questions',
          type: 'deleted',
          name: stripHtml(question.text || '') || 'Unnamed Question',
          id: question.id
        })
      }
    })

    // Find modified questions (by comparing actual content, not array position)
    currentQuestions.forEach((currentQuestion) => {
      const publishedQuestion = publishedById.get(currentQuestion.id)
      if (publishedQuestion) {
        // Compare the actual objects to see if there are real changes
        const diff = configDiffer.diff(publishedQuestion, currentQuestion)
        if (diff) {
          const modifiedFields = getModifiedFields(diff)
          // Only show as a question change if it has meaningful text
          if (modifiedFields.length > 0 && hasValidQuestionText(currentQuestion)) {
            changes.push({
              category: 'Questions',
              type: 'modified',
              name: stripHtml(currentQuestion.text || '') || 'Unnamed Question',
              id: currentQuestion.id,
              details: modifiedFields,
              diff
            })
          }
        }
      }
    })
  }

  return changes
}
export const formatChangeSummary = (changes: ConfigChange[]): string => {
  if (changes.length === 0) {
    return 'No changes detected'
  }

  const byCategory = changes.reduce<
    Record<string, { added: number; modified: number; deleted: number; items: ConfigChange[] }>
  >((acc, change) => {
    let category = acc[change.category]
    if (!category) {
      category = { added: 0, modified: 0, deleted: 0, items: [] }
      acc[change.category] = category
    }
    category[change.type]++
    category.items.push(change)
    return acc
  }, {})

  const summary: string[] = []

  Object.entries(byCategory).forEach(([category, stats]) => {
    const parts: string[] = []
    if (stats.added > 0) {
      parts.push(`${stats.added} added`)
    }
    if (stats.modified > 0) {
      parts.push(`${stats.modified} modified`)
    }
    if (stats.deleted > 0) {
      parts.push(`${stats.deleted} deleted`)
    }

    summary.push(`${category}: ${parts.join(', ')}`)
    stats.items.forEach((item) => {
      const icon = item.type === 'added' ? '✓' : item.type === 'modified' ? '~' : '✗'
      summary.push(`  ${icon} ${item.name}`)
      if (item.details && item.details.length > 0) {
        summary.push(`     (${item.details.join(', ')})`)
      }
    })
  })

  return summary.join('\n')
}

export const generateCommitMessage = (changes: ConfigChange[]): string => {
  if (changes.length === 0) {
    return 'Update configuration'
  }

  const byCategory = changes.reduce<
    Record<string, { added: number; modified: number; deleted: number; items: ConfigChange[] }>
  >((acc, change) => {
    let category = acc[change.category]
    if (!category) {
      category = { added: 0, modified: 0, deleted: 0, items: [] }
      acc[change.category] = category
    }
    category[change.type]++
    category.items.push(change)
    return acc
  }, {})

  // Generate a concise title
  const categories = Object.entries(byCategory)
  let title = ''

  const onlyCategory = categories[0]
  if (categories.length === 1 && onlyCategory !== undefined) {
    const [category, stats] = onlyCategory
    if (stats.added > 0 && stats.modified === 0 && stats.deleted === 0) {
      title = `Add ${stats.added} ${category.toLowerCase()}`
    } else if (stats.modified > 0 && stats.added === 0 && stats.deleted === 0) {
      title = `Update ${stats.modified} ${category.toLowerCase()}`
    } else if (stats.deleted > 0 && stats.added === 0 && stats.modified === 0) {
      title = `Remove ${stats.deleted} ${category.toLowerCase()}`
    } else {
      title = `Update ${category.toLowerCase()}`
    }
  } else {
    title = `Update ${categories.length} configuration sections`
  }

  // Generate bullet points
  const bullets: string[] = []
  Object.entries(byCategory).forEach(([category, stats]) => {
    const parts: string[] = []
    if (stats.added > 0) {
      parts.push(`${stats.added} added`)
    }
    if (stats.modified > 0) {
      parts.push(`${stats.modified} modified`)
    }
    if (stats.deleted > 0) {
      parts.push(`${stats.deleted} deleted`)
    }

    bullets.push(`- ${category}: ${parts.join(', ')}`)

    // Add specific items for added/deleted (up to 3 per category)
    const itemsToList = stats.items.filter((i) => i.type === 'added' || i.type === 'deleted').slice(0, 3)
    itemsToList.forEach((item) => {
      bullets.push(`  - ${item.type === 'added' ? '✓' : '✗'} ${item.name}`)
    })
  })

  return `${title}\n\n${bullets.join('\n')}`
}
